import { FilterScreenProvider } from '@/components_v2/Filtersv3/filter-store';
import { useModals } from '@/hooks/ModalManager';
import {
  Button,
  Flex,
  List,
  Skeleton,
  SkeletonText,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FileTransferProviders } from '../hooks/providers';
import { ExportItemProvider } from '../hooks/useExportItem';
import { useExports } from '../hooks/useExports';
import { ImportItemProvider } from '../hooks/useImportItem';
import { useImports } from '../hooks/useImports';
import { ExportItem } from './ExportItem';
import FileTransferFilters from './FileTransferFilters';
import { ImportItem } from './ImportItem';

// Skeleton component for export items
const ExportItemSkeleton = React.memo(() => (
  <VStack spacing="3" p="4" borderWidth="1px" borderRadius="md">
    <Skeleton height="20px" width="60%" />
    <SkeletonText noOfLines={2} spacing="2" skeletonHeight="2" width="100%" />
    <Skeleton height="16px" width="40%" />
  </VStack>
));

function ExportTabs() {
  const { t } = useTranslation();
  const { exports, onFetchMore, pageInfo, loading } = useExports();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading && exports.length >= 0) {
      setIsInitialLoad(false);
    }
  }, [loading, exports]);

  const ExportsItems: React.FC<{ loading: boolean, exports: any[] }> = ({ loading, exports }) => {
    return (
      <>
        {loading && isInitialLoad
          ? Array.from({ length: 5 }).map((_, index) => (
            <ExportItemSkeleton key={`export-skeleton-${index}`} />
          ))
          : exports.map((exportItem) => (
            <ExportItemProvider exportItem={exportItem} key={exportItem.id}>
              <ExportItem />
            </ExportItemProvider>
          ))}
      </>
    )
  }

  return (
    <Flex w="full" overflow="hidden" direction="column" gap="4">
      <FileTransferFilters type="export" />
      <List w="full" spacing="4" flex="1" overflowY="auto">
        <ExportsItems loading={loading} exports={exports} />
        <VStack w="full" gap="4" justifyContent="center">
          {loading && !isInitialLoad && <Spinner />}
          {pageInfo?.hasNextPage && (
            <Button onClick={() => onFetchMore()} size="lg" isLoading={loading}>
              {t('common.load_more')}
            </Button>
          )}
        </VStack>
      </List>
    </Flex>
  );
}

// Skeleton component for import items
const ImportItemSkeleton = React.memo(() => (
  <VStack spacing="3" p="4" borderWidth="1px" borderRadius="md">
    <Skeleton height="20px" width="60%" />
    <SkeletonText noOfLines={2} spacing="2" skeletonHeight="2" width="100%" />
    <Skeleton height="16px" width="40%" />
  </VStack>
));

function ImportsTab() {
  const { t } = useTranslation();
  const { imports, pageInfo, loading, onFetchMore } = useImports();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading && imports.length >= 0) {
      setIsInitialLoad(false);
    }
  }, [loading, imports.length]);

  const ImportsItems: React.FC<{ loading: boolean, imports: any[] }> = ({ loading, imports }) => {
    return (
      <>
        {loading && isInitialLoad
          ? Array.from({ length: 5 }).map((_, index) => (
            <ImportItemSkeleton key={`import-skeleton-${index}`} />
          ))
          : imports.map((importItem) => (
            <ImportItemProvider importItem={importItem} key={importItem.id}>
              <ImportItem />
            </ImportItemProvider>
          ))}
      </>
    )
  }

  return (
    <Flex w="full" overflow="hidden" direction="column" gap="4">
      <FileTransferFilters type="import" />
      <List w="full" spacing="4" flex="1" overflowY="auto">
        <ImportsItems loading={loading} imports={imports} />
        <VStack w="full" gap="4" justifyContent="center">
          {loading && !isInitialLoad && <Spinner />}
          {pageInfo?.hasNextPage && (
            <Button onClick={() => onFetchMore()} size="lg" isLoading={loading}>
              {t('common.load_more')}
            </Button>
          )}
        </VStack>
      </List>
    </Flex>
  );
}

export function FileTransferDrawer() {
  const { t } = useTranslation();
  return (
    <FilterScreenProvider screen="fileTransfer">
      <FileTransferProviders>
        <Tabs isLazy overflow="hidden" flex="1" variant="enclosed" size="lg">
          <TabList>
            <Tab>{t('common.exports')}</Tab>
            <Tab>{t('common.imports')}</Tab>
          </TabList>
          <TabPanels overflow="auto" pb="10" h="99%">
            <TabPanel>
              <ExportTabs />
            </TabPanel>
            <TabPanel>
              <ImportsTab />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </FileTransferProviders>
    </FilterScreenProvider>
  );
}

export function useFileTransferDrawer() {
  const modals = useModals();
  const { t } = useTranslation();

  const open = () => {
    modals.drawer({
      title: t('common.file_transfers'),
      body: <FileTransferDrawer />,
      footer: (
        <Flex>
          <Button>{t('common.close')}</Button>
        </Flex>
      ),
      size: 'lg',
    });
  };

  return open;
}
